/**
 * React Query (TanStack Query) Configuration
 *
 * Purpose: Configure React Query for data fetching, caching, and synchronization
 * Features: Automatic retries, caching, background refetching, optimistic updates
 */

import { QueryClient } from '@tanstack/react-query';

// Create a query client with custom configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Time before data is considered stale (5 minutes)
      staleTime: 5 * 60 * 1000,

      // Time before inactive queries are garbage collected (10 minutes)
      gcTime: 10 * 60 * 1000,

      // Number of retries for failed requests
      retry: 3,

      // Delay between retries (exponential backoff)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch on window focus (useful for real-time updates)
      refetchOnWindowFocus: true,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Don't refetch on mount if data is fresh
      refetchOnMount: false,

      // Keep previous data while fetching new data
      placeholderData: (previousData: any) => previousData,
    },
    mutations: {
      // Number of retries for failed mutations
      retry: 1,

      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

// Query keys for different data types
export const queryKeys = {
  // Dashboard queries
  dashboard: {
    all: ['dashboard'] as const,
    overview: () => [...queryKeys.dashboard.all, 'overview'] as const,
    certBodyPerformance: (period: string) => [...queryKeys.dashboard.all, 'cert-body-performance', period] as const,
    slaMetrics: () => [...queryKeys.dashboard.all, 'sla-metrics'] as const,
    certificationStats: (period: string) => [...queryKeys.dashboard.all, 'certification-stats', period] as const,
    activityFeed: (limit: number) => [...queryKeys.dashboard.all, 'activity-feed', limit] as const,
    forecast: () => [...queryKeys.dashboard.all, 'forecast'] as const,
    riskHeatmap: () => [...queryKeys.dashboard.all, 'risk-heatmap'] as const,
    complianceScore: () => [...queryKeys.dashboard.all, 'compliance-score'] as const,
  },

  // Security queries
  security: {
    all: ['security'] as const,
    dataBreaches: () => [...queryKeys.security.all, 'data-breaches'] as const,
    dataBreach: (id: number) => [...queryKeys.security.all, 'data-breach', id] as const,
    urgentBreaches: () => [...queryKeys.security.all, 'urgent-breaches'] as const,
    securityIncidents: () => [...queryKeys.security.all, 'security-incidents'] as const,
    securityIncident: (id: number) => [...queryKeys.security.all, 'security-incident', id] as const,
    anomalies: () => [...queryKeys.security.all, 'anomalies'] as const,
  },

  // Compliance queries
  compliance: {
    all: ['compliance'] as const,
    iso27001: () => [...queryKeys.compliance.all, 'iso27001'] as const,
    gdpr: () => [...queryKeys.compliance.all, 'gdpr'] as const,
    consents: (userId?: number) => userId
      ? [...queryKeys.compliance.all, 'consents', userId] as const
      : [...queryKeys.compliance.all, 'consents'] as const,
  },

  // Certification bodies
  certBodies: {
    all: ['cert-bodies'] as const,
    list: () => [...queryKeys.certBodies.all, 'list'] as const,
    detail: (id: number) => [...queryKeys.certBodies.all, 'detail', id] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
    detail: (id: number) => [...queryKeys.users.all, 'detail', id] as const,
  },
};

// Helper function to invalidate dashboard queries
export const invalidateDashboard = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
};

// Helper function to invalidate security queries
export const invalidateSecurity = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.security.all });
};

// Helper function to invalidate compliance queries
export const invalidateCompliance = () => {
  queryClient.invalidateQueries({ queryKey: queryKeys.compliance.all });
};
